/**
 * Mobile QC segment review (docs/app-review-api.md). A team-scoped QC reviewer is
 * served one recorded (>= JUDGED) segment at a time and resolves it skip/approve/reject.
 *
 * FIREWALL: `translation` (= Utterance.referenceTranslation) is GOLD. It appears ONLY
 * on these DTOs, returned only to a can_review holder — never on any worklist DTO.
 * Field names are exact: the app deserializes by key.
 */
import { z } from 'zod';

export type QcReviewField = 'TEXT' | 'TRANSLATION' | 'UTTERANCE_CONTEXT';
export type QcReviewCategory = 'SPELLING_TYPO' | 'GRAMMAR' | 'MISTRANSLATION' | 'AUDIO_TEXT_MISMATCH';

/** One reviewer edit, echoed back for a manual QA action (not applied to master data). */
export interface QcReviewChange {
  utteranceId: string;
  field: QcReviewField;
  before: string;
  after: string;
}

/** One spoken line in the review screen. `translation` is gold — this DTO only. */
export interface QcReviewUtterance {
  id: string;
  ordinal: number;
  speakerId: string | null;
  registerLabel: string | null;
  text: string;
  translation: string;
  utteranceContext: string | null;
}

/** The claimed segment returned by POST /me/review/next. */
export interface QcReviewSegment {
  challengeId: string;
  segmentId: string | null;
  languoidCode: string;
  domain: string | null;
  segmentContext: string | null;
  audioUrl: string;
  utterances: QcReviewUtterance[];
}

const FIELD = z.enum(['TEXT', 'TRANSLATION', 'UTTERANCE_CONTEXT']);
const CATEGORY = z.enum(['SPELLING_TYPO', 'GRAMMAR', 'MISTRANSLATION', 'AUDIO_TEXT_MISMATCH']);

export const qcReviewChangeSchema = z.object({
  utteranceId: z.string().min(1),
  field: FIELD,
  before: z.string(),
  after: z.string(),
});

export const qcReviewApproveSchema = z
  .object({
    modified: z.boolean(),
    categories: z.array(CATEGORY).default([]),
    changes: z.array(qcReviewChangeSchema).default([]),
    note: z.string().trim().max(500).nullish(),
  })
  .superRefine((v, ctx) => {
    const hasChanges = v.changes.length > 0;
    if (v.modified !== hasChanges) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '`modified` must be true iff `changes` is non-empty.',
        path: ['modified'],
      });
    }
    if (v.modified && v.categories.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one category is required when modified.',
        path: ['categories'],
      });
    }
    if (!v.modified && v.categories.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Categories are only allowed on a modified approve.',
        path: ['categories'],
      });
    }
  });

export const qcReviewRejectSchema = z.object({
  note: z.string().trim().min(1, 'A reason is required.').max(500),
});

export type QcReviewApproveInput = z.infer<typeof qcReviewApproveSchema>;
export type QcReviewRejectInput = z.infer<typeof qcReviewRejectSchema>;
